import { useState, useEffect, useRef } from 'react'
import { useInView, animate } from 'framer-motion'
import { Props_SnippetBentoStatistics } from './BentoStatistics.props'
import { Legacy_CompileMarkdown } from '@ClientBase/React/ReactSnippetMDCompiler'

const processUrl = (url: string): string => {
    let newUrl = url

    // Handle YouTube URLs
    if (url.includes('watch?v=')) {
        newUrl = url.replace('watch?v=', 'embed/')
    } else if (url.includes('youtu.be')) {
        newUrl = url.replace('youtu.be/', 'youtube.com/embed/')
    }

    // Handle Vimeo URLs
    if (url.includes('vimeo.com')) {
        const match = url.match(/vimeo\.com\/(\d+)/)
        if (match) {
            newUrl = `https://player.vimeo.com/video/${match[1]}`
        }
    }

    // Add autoplay for all URLs if not already present
    if (!newUrl.includes('autoplay=1')) {
        newUrl = `${newUrl}${newUrl.includes('?') ? '&' : '?'}autoplay=1`
    }

    return newUrl
}

const AnimatedNumber = ({ value }: { value: number }) => {
    const ref = useRef<HTMLSpanElement>(null)
    const inView = useInView(ref, { once: true })

    useEffect(() => {
        if (!inView || !ref.current) return

        const controls = animate(0, value, {
            duration: 5,
            ease: 'easeInOut',
            onUpdate(latest) {
                if (ref.current) {
                    ref.current.textContent = Math.round(latest).toLocaleString()
                }
            },
        })

        return () => controls.stop()
    }, [inView, value])

    return <span ref={ref}>0</span>
}

const SnippetBentoStatistics = (props: Props_SnippetBentoStatistics) => {
    const { heading, content, video, image1, image2, statistic } = props

    const convertedUrl = processUrl(video?.url?.value ?? '')
    const classHeadingVisibility: string = heading.visibility?.value ? `snippet-bento-statistics__heading--${heading.visibility.value}` : ''
    const [playing, setPlaying] = useState(false)
    const ref = useRef<HTMLDivElement>(null)
    const inView = useInView(ref, { once: true })

    return (
        <section className={`snippet-bento-statistics ${playing ? 'snippet-bento-statistics--playing' : ''}`}>
            <div className="snippet-bento-statistics__container">
                <h2 className={`snippet-bento-statistics__heading ${classHeadingVisibility}`}>{heading.heading?.value}</h2>
                <div className="snippet-bento-statistics__grid">
                    <div className={`snippet-bento-statistics__container-video`}>
                        {video?.url?.value && (
                        <button
                            className="snippet-bento-statistics__play-button play-button"
                            aria-label="{{js.snippet.video.button.play.aria-label}}"
                            onClick={() => setPlaying(true)}
                        />)}
                        { video.thumbnail?.value && (
                            <figure className="snippet-bento-statistics__container-thumbnail">
                                <img
                                    src={video.thumbnail?.value}
                                    alt=""
                                    className="snippet-bento-statistics__thumbnail"
                                    loading="lazy"
                                    width="710"
                                    height="510"
                                />
                            </figure>
                        )}

                        {video?.url?.value && (
                            <iframe
                                width="560"
                                height="315"
                                src={convertedUrl}
                                title="YouTube video player"
                                loading="lazy"
                                allow="accelerometer autoplay clipboard-write encrypted-media gyroscope picture-in-picture web-share"
                                referrerPolicy="strict-origin-when-cross-origin"
                                className="snippet-bento-statistics__video"
                                allowFullScreen={true}
                            ></iframe>
                        )}
                    </div>
                    <div className="snippet-bento-statistics__content">
                        {content.content?.value && Legacy_CompileMarkdown(content.content.value)}
                        { content.linkContent?.value && content.linkUrl?.value && content.linkTarget?.value && (
                            <a
                                href={content.linkUrl?.value}
                                className="snippet-bento-statistics__link"
                                target={content.linkTarget?.value === 'true' ? '_blank' : '_self'}
                                rel={content.linkTarget?.value === 'true' ? 'noopener noreferrer' : undefined}
                            >
                                {content.linkContent?.value}
                            </a>
                        )}
                    </div>
                    <div className="snippet-bento-statistics__image-block snippet-bento-statistics__image-block--first">
                        <img
                            loading="lazy"
                            src={image1.src?.value}
                            className="snippet-bento-statistics__image"
                            height="340"
                            width="410"
                            alt={image1.alt?.value ?? ''}
                        />
                    </div>
                    <div className="snippet-bento-statistics__image-block snippet-bento-statistics__image-block--second">
                        <img
                            loading="lazy"
                            src={image2.src?.value}
                            className="snippet-bento-statistics__image"
                            height="340"
                            width="260"
                            alt={image2.alt?.value ?? ''}
                        />
                    </div>
                    <div className="snippet-bento-statistics__statistic" ref={ref}>
                        { statistic.heading?.value && <div className="snippet-bento-statistics__statistic-heading">{statistic.heading.value}</div> }
                        { statistic.statistic?.value && (
                            <div className="snippet-bento-statistics__statistic-container">
                                <svg viewBox="0 0 36 36" className={`snippet-bento-statistics__statistic-chart ${inView ? 'snippet-bento-statistics__statistic-chart--visible' : ''}`}>
                                    <path className="snippet-bento-statistics__statistic-chart-background" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
                                    <path className="snippet-bento-statistics__statistic-chart-value" strokeDasharray={`${inView ? statistic.statistic.value : 0}, 100`} d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" />
                                </svg>
                                <span className="snippet-bento-statistics__statistic-percent">
                                    <AnimatedNumber value={inView ? parseInt(statistic.statistic.value) : 0}/>%
                                </span>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </section>
    )
}

export default SnippetBentoStatistics
